import { useState } from 'react'
import { Download, Inbox, Mail, Send } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { SwitchField } from '../../components/Form'
import { useToast } from '../../components/toastContext'
import {
  SETTING_KEYS,
  TOGGLE_KEYS,
  getOutlookPluginManifestUrl,
  getOutlookPluginBundleUrl,
  getOutlookPluginVstoPackageUrl,
  saveOutlookPlugin,
  savePhishingSetting,
  togglePhishingSetting,
} from './phishingService'
import { phishingSettingsStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'
import IgnoredHeadersPanel from './IgnoredHeadersPanel'
import TitleWithHint from './TitleWithHint'

const C = S.settings

/** Keep the redesigned settings cards without reverting the current V7 reporting
 * contract or removing tenant-scoped plugin downloads. */
export default function PhishingEnginePanel({ settings, onChange }) {
  const toast = useToast()
  const [phishingInterval, setPhishingInterval] = useState(settings.phishingInterval)
  const [listenerInterval, setListenerInterval] = useState(settings.listenerInterval)
  const [url, setUrl] = useState(settings.phishingUrl)
  const [outlook, setOutlook] = useState(settings.outlook)
  const [saving, setSaving] = useState('')

  const report = (outcome) => {
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || S.settingsFail.engine)
      return false
    }
    toast.success(serverText(outcome.message) || S.engineSaved)
    return true
  }

  const toggle = async (key, field, next) => {
    onChange({ [field]: next })
    try {
      if (!report(await togglePhishingSetting(key, next))) onChange({ [field]: !next })
    } catch (error) {
      onChange({ [field]: !next })
      toast.error(serverDetail(error?.message) || S.settingsFail.engine)
    }
  }

  const saveValue = async (key, value, field) => {
    setSaving(key)
    try {
      if (report(await savePhishingSetting(key, value)) && field) onChange({ [field]: value })
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.settingsFail.engine)
    } finally {
      setSaving('')
    }
  }

  const saveOutlook = async () => {
    setSaving('outlook')
    const value = { ...outlook, type: outlookTypeVal, reportingMethod: 'Ticket', deleteAfterReport: deleteAfterReportVal }
    try {
      if (report(await saveOutlookPlugin(value))) {
        setOutlook(value)
        onChange({ outlook: value })
      }
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.settingsFail.engine)
    } finally {
      setSaving('')
    }
  }

  const outlookTypeVal = ['1', '2'].includes(String(outlook.type)) ? String(outlook.type) : '1'
  const deleteAfterReportVal =
    String(outlook.deleteAfterReport ?? 'true').toLowerCase() === 'false' ? 'false' : 'true'
  const saveButton = (key, value, field) => (
    <Button variant="secondary" busy={saving === key} onClick={() => saveValue(key, value, field)}>
      {S.save}
    </Button>
  )
  const intervalHint = <>{C.delayBeforeNextRun}<br />{C.intervalHint}</>

  return (
    <div className="ph-settings">
      <Card className="ph-settings__card" icon={Send} title={<TitleWithHint title={C.engine} hint={C.engineHint} />}>
        <div className="ph-settings__body">
          <SwitchField checked={settings.phishingEnabled} onChange={(next) => toggle(TOGGLE_KEYS.phishing, 'phishingEnabled', next)} label={C.phishingEnabled} />
          <FormRow label={C.url} hint={C.urlHint}>
            {(id) => <div className="ph-settings__line"><TextField id={id} dir="ltr" value={url} onChange={setUrl} />{saveButton(SETTING_KEYS.phishingUrl, url, 'phishingUrl')}</div>}
          </FormRow>
          <FormRow label={C.phishingInterval} hint={intervalHint}>
            {(id) => <div className="ph-settings__line"><TextField id={id} dir="ltr" value={phishingInterval} onChange={setPhishingInterval} />{saveButton(SETTING_KEYS.phishingInterval, phishingInterval, 'phishingInterval')}</div>}
          </FormRow>
        </div>
      </Card>
      <Card className="ph-settings__card" icon={Inbox} title={<TitleWithHint title={C.listener} hint={C.listenerHint} />}>
        <div className="ph-settings__body">
          <SwitchField checked={settings.listenerEnabled} onChange={(next) => toggle(TOGGLE_KEYS.listener, 'listenerEnabled', next)} label={C.listenerEnabled} />
          <FormRow label={C.listenerInterval} hint={intervalHint}>
            {(id) => <div className="ph-settings__line"><TextField id={id} dir="ltr" value={listenerInterval} onChange={setListenerInterval} />{saveButton(SETTING_KEYS.listenerInterval, listenerInterval, 'listenerInterval')}</div>}
          </FormRow>
        </div>
      </Card>
      <Card className="ph-settings__card ph-settings__wide" icon={Mail} title={<TitleWithHint title={C.outlook} hint={C.outlookHint} />}
        footer={<Button busy={saving === 'outlook'} onClick={saveOutlook}>{S.save}</Button>}>
        <div className="ph-settings__outlook">
          <FormRow label={C.outlookType} hint={C.outlookTypeInfo}>
            {(id) => <Select id={id} value={outlookTypeVal} onChange={(value) => setOutlook((previous) => ({ ...previous, type: value }))}
              options={[{ value: '1', label: C.outlookTypeEmail }, { value: '2', label: C.outlookTypeWebApi }]} />}
          </FormRow>
          <FormRow label={C.outlookMethod} hint={C.outlookMethodInfo}>
            {(id) => <Select id={id} value="Ticket" disabled options={[{ value: 'Ticket', label: C.outlookMethodTicket }]} />}
          </FormRow>
          <FormRow label={C.outlookEmail} hint={C.outlookEmailInfo}>
            {(id) => <TextField id={id} dir="ltr" type="email" value={outlook.reportingEmail}
              onChange={(value) => setOutlook((previous) => ({ ...previous, reportingEmail: value }))} />}
          </FormRow>
          <FormRow label={C.outlookDelete} hint={C.outlookDeleteInfo}>
            {(id) => <Select id={id} value={deleteAfterReportVal}
              onChange={(value) => setOutlook((previous) => ({ ...previous, deleteAfterReport: value }))}
              options={[{ value: 'true', label: C.outlookDeleteYes }, { value: 'false', label: C.outlookDeleteNo }]} />}
          </FormRow>
        </div>
      </Card>
      <Card className="ph-settings__card ph-settings__wide" icon={Download} title={C.outlookStudioTitle}>
        {!settings.pluginLogoReady && <p className="ph-settings__empty" role="alert">{C.pluginLogoRequired}</p>}
        {!settings.pluginVstoReady && <p className="ph-settings__empty">{C.vstoInstallerRequired}</p>}
        <div className="ph-settings__downloads">
          <Button as="a" variant="secondary" href={settings.pluginLogoReady ? getOutlookPluginManifestUrl() : undefined}
            download={`manifest-${settings.tenantCode || 'tenant'}.xml`} aria-disabled={!settings.pluginLogoReady}
            tabIndex={settings.pluginLogoReady ? undefined : -1}><Download size={16} />{C.downloadManifest}</Button>
          <Button as="a" variant="secondary" href={settings.pluginLogoReady ? getOutlookPluginBundleUrl() : undefined}
            download={`rawam-${settings.tenantCode || 'tenant'}-plugin-bundle.zip`} aria-disabled={!settings.pluginLogoReady}
            tabIndex={settings.pluginLogoReady ? undefined : -1}><Download size={16} />{C.downloadWebBundle}</Button>
          <Button as="a" variant="secondary" href={settings.pluginVstoReady ? getOutlookPluginVstoPackageUrl() : undefined}
            download={`rawam-${settings.tenantCode || 'tenant'}-vsto-package.zip`} aria-disabled={!settings.pluginVstoReady}
            tabIndex={settings.pluginVstoReady ? undefined : -1}><Download size={16} />{C.downloadVstoPackage}</Button>
        </div>
      </Card>
      <IgnoredHeadersPanel key={settings.skipHeaders} value={settings.skipHeaders} onChange={(skipHeaders) => onChange({ skipHeaders })} />
    </div>
  )
}
